import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../../../redux/store";

import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../../redux/storeProductSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../../redux/storeCartSlice";
import { fetchAllCommercePartners } from "../../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../../redux/storeProductRatingSlice";
import { logout } from "../../../../redux/authSlice";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../../redux/notificationsSlice";
import { CurrencyContext } from "../../../../GLOBAL/CurrencyContext";
import appLogo from "../../../../assets/app/logo.png";

/* ⭐ CATEGORY SOURCE — the same collection StoreProducts saves with.
   This page is the Kitchen department and nothing else: the hanging
   rail toggles Kitchen's own categories, and everything below drills
   category → subCategory → productType → product. */
import {
  AllCategoriesFlat,
  type CategoryItem,
} from "../../../../CATEGORIES/CategoriesCollection";
import { attributesForCategory } from "../../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";

import {
  Search, X, ChevronRight, ChevronDown, Plus, Minus, Check,
  Heart, User, ShoppingCart, MapPin, Globe, AlertTriangle, LogOut,
  Utensils, UtensilsCrossed, ChefHat, Flame, Snowflake, Droplets,
  Microwave, Zap, Ruler, LayoutGrid, Home, Package, Star, ShoppingBag,
  ShoppingBasket, Sliders, CircleDot,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   SCOPE
═══════════════════════════════════════════════════════════ */
const KITCHEN_DEPT_ID = "12";

/* ═══════════════════════════════════════════════════════════
   DESIGN — a working kitchen, not a homeware catalogue.

   Enamel and metal: cream tiled backsplash, slate grout and ink,
   copper for anything you'd actually touch, flame orange saved for
   heat — deals and last stock. The signature is the plate: every
   product is shot from above onto an enamel plate, and the hob
   ring warms up behind it when you reach for it.

   Product-level accents still come from the seller's brand colour,
   so a wall of products reads as a wall of makers.
═══════════════════════════════════════════════════════════ */
const COPPER = "#B0703C";
const SLATE = "#26262B";
const ENAMEL = "#F8F5EE";
const FLAME = "#CC4B24";

const K = {
  copper: COPPER,
  copperSoft: "#F3E6D8",
  copperEdge: "#DDBE9C",
  slate: SLATE,
  flame: FLAME,
  flameSoft: "#FBE7DE",

  tile: "#F4F0E7",    // page — the backsplash
  sheet: "#FFFFFF",   // cards & modals
  sheet2: "#FBF8F2",
  grout: "#E4DED1",
  groutSoft: "#EFEAE0",
  ink: SLATE,
  inkSoft: "#4A4A52",
  muted: "#7A7469",
  faint: "#A8A093",
  steel: "#8E9AA0",
};

/* the seller's colour is the only accent the page doesn't own */
const brandAccentColor = (partner: any): string =>
  partner?.brandColor || partner?.themeColor || partner?.color || COPPER;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE HELPERS — ancestry always resolved by walking
   parentId, never by string prefixes (PT-… / PRD-… break those).
═══════════════════════════════════════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) =>
  AllCategoriesFlat.filter((c) => c.parentId === parentId);

const chainCache = new Map<string, string[]>();
const chainIdsOf = (id?: string | number | null): string[] => {
  if (id === null || id === undefined || id === "") return [];
  const key = String(id);
  const hit = chainCache.get(key);
  if (hit) return hit;
  const chain: string[] = [];
  let cur = CAT_MAP.get(key);
  while (cur) {
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(key, chain);
  return chain;
};

const catName = (id?: string | number | null) =>
  id === null || id === undefined ? "" : CAT_MAP.get(String(id))?.name ?? "";

/** Path with the department stripped — everything here is Kitchen. */
const catPathLabel = (id?: string | number | null) =>
  chainIdsOf(id).filter((cid) => cid !== KITCHEN_DEPT_ID).map(catName).join(" / ");

/** Kitchen's own categories — the hanging rail. */
const KITCHEN_CATEGORIES: CategoryItem[] = childrenOf(KITCHEN_DEPT_ID);

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES — same bridge StoreProducts uses.
═══════════════════════════════════════════════════════════ */
const toAttributeCategoryId = (id?: string | number | null): string => {
  if (id === null || id === undefined || id === "") return "";
  const chain = chainIdsOf(id).map((cid) => CAT_MAP.get(cid)!).filter(Boolean);
  const anchor = [...chain].reverse().find((c) => c.level === "category" || c.level === "department");
  return anchor ? `0${anchor.id}` : "";
};

const attrCache = new Map<string, any[]>();
const resolveAttributes = (categoryId?: string | number | null): any[] => {
  if (categoryId === null || categoryId === undefined || categoryId === "") return [];
  const key = String(categoryId);
  const hit = attrCache.get(key);
  if (hit) return hit;

  const chain = [...chainIdsOf(categoryId)].reverse();
  const keys = [toAttributeCategoryId(categoryId), ...chain.flatMap((c) => [`0${c}`, c])].filter(Boolean);

  let found: any[] = [];
  for (const k of keys) {
    try {
      const defs = attributesForCategory(k as any);
      if (defs && defs.length) { found = defs; break; }
    } catch { /* unknown key — keep walking up */ }
  }
  attrCache.set(key, found);
  return found;
};

const resolveAttrValue = (attr: any, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  const label = (optId: any) => {
    const opt = attr.options?.find((o: any) => String(o.id) === String(optId));
    return opt ? (opt.name || opt.standard || String(optId)) : String(optId);
  };
  if (Array.isArray(value)) return value.map(label).join(", ");
  if (attr.options?.length) return label(value);
  return `${value}${attr.unit ? ` ${attr.unit}` : ""}`;
};

const isEmptyValue = (v: any) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/** Attributes whose options make them a buyable variant (Colour, Size…). */
const isVariantAttr = (attr: any) =>
  (attr?.inputType === "select" || !attr?.inputType) &&
  Array.isArray(attr?.options) && attr.options.length > 0;

const variantAttrsForItem = (item: any) => resolveAttributes(item?.categoryId).filter(isVariantAttr);
const hasVariants = (item: any) => variantAttrsForItem(item).length > 0;

interface Fact { name: string; value: string }
/** Everything the seller actually filled in. */
const factsOf = (item: any): Fact[] => {
  const saved = item?.attributes ?? {};
  return resolveAttributes(item?.categoryId)
    .filter((def: any) => !isEmptyValue(saved[String(def.id)]))
    .map((def: any) => ({ name: def.name, value: resolveAttrValue(def, saved[String(def.id)]) }))
    .filter((f) => f.value);
};

const firstFact = (item: any, re: RegExp): Fact | null => {
  const saved = item?.attributes ?? {};
  const def = resolveAttributes(item?.categoryId)
    .find((d: any) => re.test(d.name || "") && !isEmptyValue(saved[String(d.id)]));
  if (!def) return null;
  const value = resolveAttrValue(def, saved[String(def.id)]);
  return value ? { name: def.name, value } : null;
};

/* Cookware is bought on four questions, in this order:
   what's it made of, how big is it, will it work on my hob,
   and can I put it in the dishwasher. */
const MATERIAL_RE = /material|finish|coating|steel|iron|ceramic|non.?stick|wood/i;
const DIAMETER_RE = /diameter|width|size|length|Ø/i;
const CAPACITY_RE = /capacity|volume|litre|liter|contents/i;
const PIECES_RE = /piece|pcs|set|count|items? included/i;

const materialFactOf = (item: any) => firstFact(item, MATERIAL_RE);
const piecesFactOf = (item: any) => firstFact(item, PIECES_RE);

/** "Ø 24 cm · 2.8 L · 5 pcs" — whatever the seller gave us. */
const sizeLineOf = (item: any): string | null => {
  const d = firstFact(item, DIAMETER_RE);
  const c = firstFact(item, CAPACITY_RE);
  const p = piecesFactOf(item);
  const parts: string[] = [];
  if (d) parts.push(`Ø ${d.value}`);
  if (c) parts.push(c.value);
  if (p && !d && !c) parts.push(p.value);
  return parts.length ? parts.join(" · ") : null;
};

/* ── "Works with" — the gotchas that send cookware back ── */
const COMPAT: { key: string; re: RegExp; label: string; icon: React.ReactNode }[] = [
  { key: "induction", re: /induction/i, label: "Induction", icon: <Zap size={10} /> },
  { key: "oven", re: /oven.?safe|oven.?proof|oven/i, label: "Oven", icon: <Flame size={10} /> },
  { key: "dishwasher", re: /dishwasher/i, label: "Dishwasher", icon: <Droplets size={10} /> },
  { key: "microwave", re: /microwave/i, label: "Microwave", icon: <Microwave size={10} /> },
  { key: "freezer", re: /freezer|frozen/i, label: "Freezer", icon: <Snowflake size={10} /> },
];

/** Only returns a badge when the seller said yes — never guesses. */
const compatOf = (item: any) => {
  const saved = item?.attributes ?? {};
  const defs = resolveAttributes(item?.categoryId);
  return COMPAT.filter((c) =>
    defs.some((def: any) => {
      if (!c.re.test(def.name || "")) return false;
      const raw = saved[String(def.id)];
      if (isEmptyValue(raw)) return false;
      const v = resolveAttrValue(def, raw);
      return !!v && !/^(no|false|none|not)$/i.test(v.trim());
    })
  );
};

type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";

const isOutOfStock = (item: any) => item?.available === false || (item?.stock ?? 0) <= 0;

/* ═══════════════════════════════════════════════════════════
   LOCATION TIERS
═══════════════════════════════════════════════════════════ */
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null =>
  country ? COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null : null;
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Karla:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-thumb { background: ${K.copperEdge}; border-radius: 99px; }
input, button, select { font-family: inherit; }
.ks :focus-visible { outline: 2px solid ${COPPER}; outline-offset: 2px; border-radius: 6px; }

@keyframes ksIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes ksBlink { 0%,100% { opacity: 1; } 50% { opacity: .3; } }
@keyframes ksSwing { 0%,100% { transform: rotate(-1.2deg); } 50% { transform: rotate(1.2deg); } }

/* tiled backsplash under everything */
.ks {
  min-height: 100vh; background: ${K.tile}; color: ${K.ink};
  font-family: 'Karla', sans-serif; padding-bottom: 96px; position: relative;
  background-image:
    linear-gradient(${K.grout} 1px, transparent 1px),
    linear-gradient(90deg, ${K.grout} 1px, transparent 1px);
  background-size: 72px 36px;
}
.ks-mono { font-family: 'IBM Plex Mono', monospace; font-variant-numeric: tabular-nums; }
.ks-disp { font-family: 'Instrument Serif', Georgia, serif; font-weight: 400; letter-spacing: -.01em; }

/* ── header: a copper hairline over the tiles ── */
.ks-rail { position: sticky; top: 0; z-index: 200;
  background: rgba(248,245,238,.93); backdrop-filter: blur(14px);
  border-bottom: 1px solid ${K.grout}; }
.ks-edge { height: 3px; background: linear-gradient(90deg, ${K.copperEdge}, ${COPPER} 40%, ${K.copperEdge}); }

.ks-hbtn { display:flex; align-items:center; gap:6px; background:${K.sheet};
  border:1px solid ${K.grout}; color:${K.inkSoft}; font-size:12px; font-weight:500;
  padding:9px 13px; border-radius:8px; cursor:pointer; flex-shrink:0; white-space:nowrap;
  transition: border-color .16s, color .16s, background .16s; }
.ks-hbtn:hover { border-color:${K.copperEdge}; background:${K.copperSoft}; color:${K.ink}; }

.ks-search { display:flex; align-items:center; flex:1; min-width:0; background:${K.sheet};
  border:1px solid ${K.grout}; border-radius:8px; overflow:hidden; transition:border-color .16s; }
.ks-search:focus-within { border-color:${COPPER}; }
.ks-search input { flex:1; min-width:0; background:none; border:none; outline:none;
  padding:10px 12px; font-size:13px; color:${K.ink}; }
.ks-search input::placeholder { color:${K.faint}; }

/* ── 🪝 THE RAIL — categories hang off a copper bar ── */
.ks-hang { position:relative; display:flex; gap:10px; overflow-x:auto; scrollbar-width:none;
  padding:13px 2px 10px; }
.ks-hang::before { content:""; position:absolute; top:5px; left:0; right:0; height:2px;
  background: linear-gradient(90deg, ${K.copperEdge}, ${COPPER}, ${K.copperEdge});
  border-radius:2px; }
.ks-tag {
  position:relative; flex-shrink:0; display:flex; align-items:center; gap:8px; white-space:nowrap;
  padding:10px 15px; cursor:pointer; font-size:12.5px; font-weight:500; color:${K.inkSoft};
  background:${K.sheet}; border:1px solid ${K.grout}; border-radius:3px 3px 8px 8px;
  box-shadow: 0 2px 5px rgba(38,38,43,.07);
  transform-origin: 50% -8px;
  transition: background .16s, color .16s, border-color .16s, transform .16s;
}
/* the hook */
.ks-tag::before { content:""; position:absolute; top:-9px; left:50%; margin-left:-4px;
  width:8px; height:8px; border:2px solid ${COPPER}; border-radius:50%;
  border-top-color:transparent; background:transparent; }
.ks-tag:hover { background:${K.copperSoft}; border-color:${K.copperEdge}; color:${K.ink};
  animation: ksSwing 1.4s ease-in-out infinite; }
.ks-tag.on { background:${SLATE}; border-color:${SLATE}; color:${ENAMEL}; }
.ks-tag .ks-dot { width:5px; height:5px; border-radius:50%; background:${K.copperEdge}; flex-shrink:0; }
.ks-tag.on .ks-dot { background:${COPPER}; animation: ksBlink 1.8s ease-in-out infinite; }

/* ── 🍽 THE PLATE — the signature. Shot from above onto enamel,
      and the hob ring warms up when you reach for it. ── */
.ks-card { background:${K.sheet}; border:1px solid ${K.grout}; border-radius:12px;
  overflow:hidden; cursor:pointer; display:flex; flex-direction:column;
  animation: ksIn .26s ease both; transition: border-color .2s, transform .2s, box-shadow .2s; }
.ks-card:hover { transform: translateY(-4px); }

.ks-plate { position:relative; width:100%; aspect-ratio:1/1; overflow:hidden;
  background:${K.sheet2}; display:flex; align-items:center; justify-content:center; }
/* the hob ring, off until you hover */
.ks-plate::before { content:""; position:absolute; z-index:0; left:50%; top:50%;
  width:74%; height:74%; margin:-37% 0 0 -37%; border-radius:50%;
  background: radial-gradient(closest-side, ${FLAME}38, ${FLAME}00 72%);
  opacity:0; transition: opacity .3s ease; }
.ks-card:hover .ks-plate::before { opacity:1; }
/* the enamel plate itself */
.ks-plate::after { content:""; position:absolute; z-index:1; left:50%; top:50%;
  width:86%; height:86%; margin:-43% 0 0 -43%; border-radius:50%;
  background:${ENAMEL}; border:1px solid ${K.grout};
  box-shadow: inset 0 0 0 5px #fff, inset 0 0 0 6px ${K.groutSoft}, 0 2px 10px rgba(38,38,43,.07); }
.ks-plate img { position:relative; z-index:2; width:62%; height:62%; object-fit:contain;
  filter: drop-shadow(0 8px 12px rgba(38,38,43,.18)); transition: transform .28s ease; }
.ks-card:hover .ks-plate img { transform: scale(1.06) rotate(-2deg); }

.ks-chip { display:inline-flex; align-items:center; gap:4px; font-size:9.5px; font-weight:500;
  color:${K.muted}; background:${K.sheet2}; border:1px solid ${K.grout};
  border-radius:6px; padding:3px 8px; white-space:nowrap; }
.ks-chip.mat { color:${K.ink}; background:${K.copperSoft}; border-color:${K.copperEdge}; }
.ks-chip.hot { color:${K.flame}; background:${K.flameSoft}; border-color:#EFC9B8; }

.ks-flag { position:absolute; z-index:3; top:10px; font-size:9.5px; font-weight:700;
  letter-spacing:.04em; padding:3px 8px; border-radius:6px; }

.ks-add { width:100%; height:40px; border:1px solid; border-radius:8px; background:transparent;
  display:flex; align-items:center; justify-content:center; gap:7px; cursor:pointer;
  font-size:12px; font-weight:700; transition: background .16s, color .16s; }
.ks-step { display:flex; align-items:center; justify-content:space-between;
  height:40px; border:1px solid; border-radius:8px; padding:0 5px; }
.ks-step button { width:28px; height:28px; border-radius:6px; border:none; cursor:pointer;
  display:flex; align-items:center; justify-content:center; }

.ks-panel { background:${K.sheet}; border:1px solid ${K.grout}; border-radius:12px; }
.ks-node { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px;
  cursor:pointer; font-size:13px; color:${K.inkSoft}; transition: background .14s, color .14s; }
.ks-node:hover { background:${K.copperSoft}; color:${K.ink}; }
.ks-node.on { background:${SLATE}; color:${ENAMEL}; }

.ks-tile { background:${K.sheet2}; border:1px solid ${K.grout}; border-radius:10px;
  padding:12px 10px; cursor:pointer; display:flex; flex-direction:column; align-items:center;
  gap:8px; text-align:center; transition: border-color .16s, background .16s, transform .16s; }
.ks-tile:hover { border-color:${K.copperEdge}; background:${K.copperSoft}; transform: translateY(-2px); }
.ks-tile.on { border-color:${COPPER}; background:${K.copperSoft}; }

.ks-tab { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px;
  font-weight:500; cursor:pointer; color:${K.faint}; padding:4px 10px; background:none; border:none; }
.ks-tab.on { color:${K.ink}; }
.ks-nav { display:flex; align-items:center; gap:11px; padding:12px 16px; cursor:pointer;
  font-size:13.5px; font-weight:500; color:${K.inkSoft}; background:none; width:100%;
  text-align:left; border:none; border-bottom:1px solid ${K.grout}; }
.ks-nav:hover { background:${K.copperSoft}; color:${K.ink}; }

@media (prefers-reduced-motion: reduce) {
  .ks-card, .ks-tag:hover, .ks-tag.on .ks-dot { animation: none !important; }
  .ks-card, .ks-plate img, .ks-plate::before, .ks-tile, .ks-tag { transition: none; }
}
`;

const S: Record<string, React.CSSProperties> = {
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(224px,1fr))", gap: 16 },
  mobileGrid: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  tabs: {
    position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 62,
    background: "rgba(255,255,255,.96)", backdropFilter: "blur(12px)",
    borderTop: `1px solid ${K.grout}`, display: "flex",
    justifyContent: "space-around", alignItems: "center", zIndex: 150,
  },
};

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const KitchenStore: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);
  const cur = symbol ?? "€";
  const fx = (n: number) => (n ?? 0) * (rate ?? 1);
  const price = (n: number) => `${cur}${fx(n).toFixed(2)}`;

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId = authUser?.userId ?? "";

  const filteredListings = useSelector((s: RootState) => s.storeProducts.filteredProducts);
  const cartItems = useSelector((s: RootState) => (s as any).storeCart?.items ?? []);
  const ratingSummaries = useSelector((s: RootState) => (s as any).storeProductRatings?.summaries ?? {});
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [catOpen, setCatOpen] = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState<SortKey>("default");
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  /* the category is the master toggle — this page is one department */
  const [activeCat, setActiveCat] = useState<string>(KITCHEN_CATEGORIES[0]?.id ?? KITCHEN_DEPT_ID);
  const cat = useMemo(
    () => KITCHEN_CATEGORIES.find((c) => c.id === activeCat) ?? KITCHEN_CATEGORIES[0],
    [activeCat]
  );
  const [activeSub, setActiveSub] = useState<string | null>(null);
  const [activeDeep, setActiveDeep] = useState<string | null>(null);

  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
  const [browseOpen, setBrowseOpen] = useState(true);

  /* kitchen's own filter: the compatibility gotchas */
  const [compatFilter, setCompatFilter] = useState<Set<string>>(new Set());

  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [userLat, setUserLat] = useState<number | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [qty, setQty] = useState(1);
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, any>>({});
  const [wished, setWished] = useState<Set<string>>(new Set());

  const cartTotal = cartItems.reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCity, userCountry]);

  /* ── effects ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedProducts() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllCommercePartners() as any); }, [dispatch]);
  useEffect(() => { dispatch(filterLocalProductsByCountry(null) as any); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId) as any);
    return () => { dispatch(stopUserNotificationsListener() as any); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true);
      dispatch(filterLocalProductsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
              (results: any, status: string) => {
                if (status === "OK" && results?.[0]) {
                  const comps = results[0].address_components;
                  const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                  setUserCity(get("locality") || get("administrative_area_level_2"));
                  const country = get("country"); setUserCountry(country);
                  if (country) dispatch(filterLocalProductsByCountry(country) as any);
                }
              });
          }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          dispatch(filterLocalProductsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName })
        .then((r) => { r.onchange = () => { if (r.state === "granted") detect(); }; })
        .catch(() => {});
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  useEffect(() => {
    if (!detailItem) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [detailItem]);

  /* ── actions ── */
  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveSub(null); setActiveDeep(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry) as any);
  };

  const resetAllFilters = () => {
    setActiveSub(null); setActiveDeep(null); setActiveSort("default"); setSearchQuery("");
    setCompatFilter(new Set());
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  };

  const handleCatSelect = (id: string) => {
    setActiveCat(id); setActiveSub(null); setActiveDeep(null); setOpenNodes(new Set());
  };
  const handleSubSelect = (id: string) => { setActiveSub((p) => (p === id ? null : id)); setActiveDeep(null); };
  const handleDeepSelect = (id: string) => setActiveDeep((p) => (p === id ? null : id));

  const toggleCompat = (key: string) =>
    setCompatFilter((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  const openDetail = (item: any) => { setDetailItem(item); setQty(1); setSelectedVariants({}); };
  const closeDetail = () => setDetailItem(null);

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    if (hasVariants(item)) { openDetail(item); return; }
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({
      listingId: item.listingId, name: item.name!, rate: item.rate!,
      photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
    } as any));
  };

  const handleDetailContinue = (toCheckout: boolean) => {
    if (!detailItem || isOutOfStock(detailItem)) return;
    const vDefs = variantAttrsForItem(detailItem);
    if (vDefs.some((a: any) => selectedVariants[String(a.id)] == null)) return;

    ensureCartBrandCompat(detailItem.brandId);
    dispatch(addToCart({
      listingId: detailItem.listingId, name: detailItem.name!,
      rate: detailItem.rate!, qty,
      photo: detailItem.photos?.[0],
      hostId: detailItem.hostId, brandId: detailItem.brandId,
      selectedVariants: { ...selectedVariants },
    } as any));
    closeDetail(); setQty(1); setSelectedVariants({});
    if (toCheckout) goToCheckout();
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const goToCheckout = () => navigate(`/pay/${userId}`, { state: { items: cartItems } });
  const goToBrand = (id: string) => { if (id && id !== "unknown") navigate(`/brand/${id}`); };
  const handleLogout = async () => { await dispatch(logout() as any); navigate("/AuthSwitch"); };

  /* ═══════════════════════════════════
     FILTERING — always inside Kitchen
  ═══════════════════════════════════ */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings || []).filter((l: any) => {
      if (!l.isPublished) return false;
      if (isOutOfStock(l)) return false;
      const chain = chainIdsOf(l.categoryId);
      if (!chain.includes(KITCHEN_DEPT_ID)) return false;
      if (!chain.includes(activeCat)) return false;
      if (activeSub && !chain.includes(activeSub)) return false;
      if (activeDeep && !chain.includes(activeDeep)) return false;
      if (compatFilter.size) {
        const have = new Set(compatOf(l).map((c) => c.key));
        for (const need of compatFilter) if (!have.has(need)) return false;
      }
      if (q) {
        const factText = factsOf(l).map((f) => `${f.name} ${f.value}`).join(" ");
        const hay = `${l.name ?? ""} ${l.description ?? ""} ${l.category ?? ""} ${l.vendor ?? ""} ${(l.tags ?? []).join(" ")} ${factText}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (activeSort === "price_asc") list = [...list].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0));
    else if (activeSort === "price_desc") list = [...list].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    else if (activeSort === "name_asc") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "newest") list = [...list].sort((a, b) => (Date.parse(b.createdAt ?? 0) || 0) - (Date.parse(a.createdAt ?? 0) || 0));

    return list;
  }, [filteredListings, activeCat, activeSub, activeDeep, searchQuery, activeSort, compatFilter]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map((bId) => ({ brandId: bId, items: map.get(bId)! }));
    if (locationActive) {
      groups = groups.filter((g) => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
  }, [filteredProducts, showGlobal, brandTier, locationActive]);

  const visibleCount = useMemo(
    () => productsByBrand.reduce((sum, g) => sum + g.items.length, 0),
    [productsByBrand]
  );

  const activeFilterName = activeDeep ? catName(activeDeep) : activeSub ? catName(activeSub) : null;
  const catChildren = useMemo(() => childrenOf(activeCat), [activeCat]);

  /* ═══════════════════════════════════
     RAIL  (plain function calls so the search keeps focus)
  ═══════════════════════════════════ */
  const railRow = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 9, padding: isMobile ? "11px 14px" : "13px 22px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: K.copperSoft, border: `1px solid ${K.copperEdge}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <UtensilsCrossed size={15} color={COPPER} />
        </div>
        {!isMobile && <span className="ks-disp" style={{ fontSize: 19, color: K.ink }}>Kitchen</span>}
      </div>

      {locationDenied ? (
        <button className="ks-hbtn" onClick={() => setLocationModalOpen(true)} aria-label="Location off">
          <AlertTriangle size={13} />
        </button>
      ) : !userCountry ? (
        <div className="ks-hbtn">
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: COPPER, animation: "ksBlink 1.2s infinite" }} />
          {!isMobile && <span>Locating…</span>}
        </div>
      ) : (
        <button onClick={handleToggleGlobal} className="ks-hbtn">
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          {!isMobile && <span>{showGlobal ? "Global" : userCity || userCountry}</span>}
        </button>
      )}

      <div className="ks-search">
        <span style={{ padding: "0 4px 0 11px", display: "flex" }}><Search size={14} color={K.faint} /></span>
        <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search pans, knives, mixers…" aria-label="Search kitchen" />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} aria-label="Clear search"
            style={{ background: "none", border: "none", cursor: "pointer", padding: "0 10px", color: K.faint, display: "flex" }}>
            <X size={13} />
          </button>
        )}
      </div>

      <button onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen((v) => !v))} className="ks-hbtn">
        <LayoutGrid size={14} />{!isMobile && <span>Browse</span>}
      </button>

      <button onClick={goToCheckout} className="ks-hbtn" style={{ position: "relative" }}>
        <ShoppingCart size={14} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal}` : "Cart"}</span>}
        {cartTotal > 0 && (
          <span className="ks-mono" style={{ position: "absolute", top: -6, right: -6, background: FLAME, color: "#fff", fontSize: 9, minWidth: 17, height: 17, padding: "0 4px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>

      {!isMobile && (
        <div style={{ position: "relative" }}>
          <button onClick={() => setProfileMenuOpen((v) => !v)} className="ks-hbtn" aria-label="Profile"><User size={14} /></button>
          <AnimatePresence>
            {profileMenuOpen && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 196, background: K.sheet, border: `1px solid ${K.grout}`, borderRadius: 12, overflow: "hidden", zIndex: 400, boxShadow: "0 18px 44px rgba(38,38,43,.16)" }}>
                <button className="ks-nav" onClick={() => navigate(`/profile/${userId}`)}><User size={14} /> Profile</button>
                <button className="ks-nav" onClick={() => navigate(`/clientdashboard/${userId}`)}><ShoppingBag size={14} /> Orders</button>
                <button className="ks-nav" style={{ borderBottom: "none", color: K.flame }} onClick={handleLogout}><LogOut size={14} /> Sign out</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     🪝 HANGING RAIL — Kitchen's categories
  ═══════════════════════════════════ */
  const hangingRail = () => (
    <div className="ks-hang">
      {KITCHEN_CATEGORIES.map((c) => {
        const on = c.id === activeCat;
        return (
          <button key={c.id} className={`ks-tag${on ? " on" : ""}`} onClick={() => handleCatSelect(c.id)}>
            <span className="ks-dot" />
            <span style={{ display: "flex", opacity: on ? 1 : .72 }}>{c.icon}</span>
            {c.name}
          </button>
        );
      })}
    </div>
  );

  const heading = () => (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 14, flexWrap: "wrap", padding: "4px 2px 14px" }}>
      <div>
        <div className="ks-mono" style={{ fontSize: 10, letterSpacing: ".22em", color: K.faint, textTransform: "uppercase", marginBottom: 4 }}>
          Kitchen / {cat?.name ?? "All"}
        </div>
        <h1 className="ks-disp" style={{ fontSize: isMobile ? 32 : 46, color: K.ink, lineHeight: 1 }}>
          {activeFilterName ?? cat?.name ?? "Kitchen"}
        </h1>
      </div>
      <div className="ks-mono" style={{ fontSize: 11, color: K.muted, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span><b style={{ color: K.ink }}>{visibleCount}</b> pieces</span>
        <span style={{ color: K.grout }}>│</span>
        <span><b style={{ color: K.ink }}>{productsByBrand.length}</b> makers</span>
        <span style={{ color: K.grout }}>│</span>
        <span>{locationDenied ? "worldwide" : showGlobal ? "global" : userCity ?? userCountry ?? "nearby"}</span>
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     WORKS-WITH FILTER — the gotchas, as a filter
  ═══════════════════════════════════ */
  const compatBar = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
      <span className="ks-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: K.faint, textTransform: "uppercase", marginRight: 2 }}>
        Works with
      </span>
      {COMPAT.map((c) => {
        const on = compatFilter.has(c.key);
        return (
          <button key={c.key} onClick={() => toggleCompat(c.key)}
            style={{
              display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8,
              border: `1px solid ${on ? COPPER : K.grout}`, background: on ? K.copperSoft : K.sheet,
              color: on ? K.ink : K.muted, fontSize: 11.5, fontWeight: 500, cursor: "pointer",
            }}>
            {c.icon}{c.label}
            {on && <X size={11} />}
          </button>
        );
      })}
    </div>
  );

  /* ═══════════════════════════════════
     BROWSE TREE
  ═══════════════════════════════════ */
  const TreeNode: React.FC<{ node: CategoryItem; depth: number }> = ({ node, depth }) => {
    const kids = childrenOf(node.id);
    const isOpen = openNodes.has(node.id);
    const picked = activeSub === node.id || activeDeep === node.id;
    return (
      <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
        <div className={`ks-node${picked ? " on" : ""}`}
          onClick={() => {
            if (depth === 0) handleSubSelect(node.id);
            else handleDeepSelect(node.id);
            if (kids.length) toggleNode(node.id);
            else closePanel();
          }}>
          <span style={{ display: "flex", color: picked ? COPPER : K.faint, flexShrink: 0 }}>{node.icon}</span>
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</span>
          {kids.length > 0 && (
            <motion.span animate={{ rotate: isOpen ? 90 : 0 }} style={{ display: "flex", flexShrink: 0 }}>
              <ChevronRight size={13} color={picked ? COPPER : K.faint} />
            </motion.span>
          )}
          {picked && kids.length === 0 && <Check size={13} color={COPPER} style={{ flexShrink: 0 }} />}
        </div>
        <AnimatePresence>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              <div style={{ borderLeft: `1px solid ${K.grout}`, marginLeft: 12, paddingLeft: 4 }}>
                {kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const browsePanel = () => (
    <div className="ks-panel" style={{ padding: 14 }}>
      <div className="ks-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: K.faint, textTransform: "uppercase", marginBottom: 10 }}>
        {cat?.name ?? "Browse"}
      </div>
      {catChildren.length === 0
        ? <p style={{ fontSize: 12, color: K.faint }}>Nothing deeper in this drawer yet.</p>
        : catChildren.map((c) => <TreeNode key={c.id} node={c} depth={0} />)}
    </div>
  );

  const mobileBrowseSheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(38,38,43,.5)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", background: K.tile, borderTop: `1px solid ${K.grout}`, borderRadius: "14px 14px 0 0", zIndex: 401, overflowY: "auto" }}>
            <div style={{ width: 34, height: 3, background: K.copperEdge, borderRadius: 2, margin: "12px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", padding: "12px 18px 10px", borderBottom: `1px solid ${K.grout}` }}>
              <span className="ks-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: K.muted }}>BROWSE</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", display: "flex" }}>
                <X size={15} color={K.muted} />
              </button>
            </div>
            <div style={{ padding: 16 }}>{hangingRail()}<div style={{ height: 12 }} />{browsePanel()}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     DRAWER TILES
  ═══════════════════════════════════ */
  const drawerTiles = () => {
    if (!catChildren.length) return null;
    return (
      <div style={{ marginBottom: 24 }}>
        <div onClick={() => setBrowseOpen((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 12 }}>
          <Sliders size={13} color={K.muted} />
          <span className="ks-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: K.muted, textTransform: "uppercase" }}>Narrow it down</span>
          <motion.span animate={{ rotate: browseOpen ? 0 : -90 }} style={{ display: "flex" }}>
            <ChevronDown size={15} color={K.faint} />
          </motion.span>
        </div>
        <AnimatePresence initial={false}>
          {browseOpen && (
            <motion.div key={activeCat} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(3,1fr)" : "repeat(auto-fill,minmax(128px,1fr))", gap: 10 }}>
                <button className={`ks-tile${activeSub === null ? " on" : ""}`}
                  onClick={() => { setActiveSub(null); setActiveDeep(null); }}>
                  <CircleDot size={17} color={activeSub === null ? COPPER : K.muted} />
                  <span style={{ fontSize: 11, fontWeight: 500, color: activeSub === null ? K.ink : K.inkSoft }}>All</span>
                </button>
                {catChildren.map((node) => {
                  const on = activeSub === node.id;
                  return (
                    <button key={node.id} className={`ks-tile${on ? " on" : ""}`} onClick={() => handleSubSelect(node.id)}>
                      <span style={{ display: "flex", fontSize: 17, color: on ? COPPER : K.muted }}>{node.icon}</span>
                      <span style={{ fontSize: 11, fontWeight: 500, color: on ? K.ink : K.inkSoft, lineHeight: 1.2 }}>{node.name}</span>
                    </button>
                  );
                })}
              </div>

              {activeSub && childrenOf(activeSub).length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                  {childrenOf(activeSub).map((node) => {
                    const on = activeDeep === node.id;
                    return (
                      <button key={node.id} onClick={() => handleDeepSelect(node.id)}
                        style={{
                          display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8,
                          border: `1px solid ${on ? COPPER : K.grout}`, background: on ? K.copperSoft : K.sheet,
                          color: on ? K.ink : K.inkSoft, fontSize: 11.5, fontWeight: 500, cursor: "pointer",
                        }}>
                        {on && <Check size={11} color={COPPER} />}
                        {node.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     MAKER ROW
  ═══════════════════════════════════ */
  const makerRow = (brandId: string) => {
    const partner = brandMap.get(brandId);
    const name = partner?.name || "Maker";
    const logo = partner?.image || appLogo;
    const accent = brandAccentColor(partner);
    const city = partner?.address?.city;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <button onClick={() => goToBrand(brandId)} aria-label={`Open ${name}`}
          style={{ width: 30, height: 30, borderRadius: 8, overflow: "hidden", flexShrink: 0, border: `1px solid ${K.grout}`, background: K.sheet, padding: 0, cursor: "pointer" }}>
          <img src={logo} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span onClick={() => goToBrand(brandId)} className="ks-disp"
          style={{ fontSize: 18, color: K.ink, cursor: "pointer", borderBottom: `2px solid ${accent}`, paddingBottom: 1 }}>
          {name}
        </span>
        {city && <span className="ks-mono" style={{ fontSize: 10.5, color: K.faint }}>{city}</span>}
        <div style={{ flex: 1, height: 1, background: K.grout }} />
        <button onClick={() => goToBrand(brandId)} aria-label={`Open ${name}`}
          style={{ width: 26, height: 26, borderRadius: 7, border: `1px solid ${K.grout}`, background: K.sheet, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={14} color={accent} />
        </button>
      </div>
    );
  };

  /* ═══════════════════════════════════
     🍽 PLATE CARD
  ═══════════════════════════════════ */
  const plateCard = (item: any) => {
    const partner = brandMap.get(item.brandId);
    const accent = brandAccentColor(partner);
    const q = getQty(item.listingId);
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;
    const material = materialFactOf(item);
    const sizeLine = sizeLineOf(item);
    const pieces = piecesFactOf(item);
    const compat = compatOf(item).slice(0, 4);
    const configurable = hasVariants(item);
    const isWished = wished.has(item.listingId);
    const low = (item.stock ?? 0) > 0 && (item.stock ?? 0) <= 5;

    return (
      <div key={item.listingId} className="ks-card" onClick={() => openDetail(item)}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = accent;
          el.style.boxShadow = `0 0 0 1px ${accent}26, 0 18px 40px rgba(38,38,43,.14)`;
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = K.grout;
          el.style.boxShadow = "none";
        }}>
        <div className="ks-plate">
          {item.photos?.[0]
            ? <img src={item.photos[0]} alt={item.name}
                onError={(e) => { const el = e.currentTarget as HTMLImageElement; el.src = appLogo; el.style.opacity = "0.25"; }} />
            : <Utensils size={30} color={K.faint} style={{ position: "relative", zIndex: 2 }} />}

          {discount && (
            <span className="ks-flag ks-mono" style={{ left: 10, background: FLAME, color: "#fff" }}>−{discount}%</span>
          )}
          {!discount && low && (
            <span className="ks-flag ks-mono" style={{ left: 10, background: K.sheet, color: K.flame, border: `1px solid ${K.grout}` }}>
              {item.stock} left
            </span>
          )}
          {pieces && (
            <span className="ks-flag ks-mono" style={{ right: 10, background: SLATE, color: ENAMEL }}>{pieces.value}</span>
          )}
          <button onClick={(e) => toggleWish(item.listingId, e)} aria-label="Save"
            style={{ position: "absolute", zIndex: 3, bottom: 10, right: 10, width: 27, height: 27, borderRadius: 7, background: "rgba(255,255,255,.92)", border: `1px solid ${K.grout}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <Heart size={12} color={isWished ? accent : K.muted} fill={isWished ? accent : "none"} />
          </button>
        </div>

        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 7, flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: K.ink, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: 35 }}>
            {item.name}
          </div>

          <div className="ks-mono" style={{ fontSize: 10.5, color: K.muted, display: "flex", alignItems: "center", gap: 5 }}>
            {sizeLine ? <><Ruler size={10} />{sizeLine}</> : (
              <span style={{ letterSpacing: ".06em", textTransform: "uppercase", fontSize: 9.5, color: K.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {catPathLabel(item.categoryId) || item.category || "kitchen"}
              </span>
            )}
          </div>

          {(material || compat.length > 0) && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {material && <span className="ks-chip mat">{material.value}</span>}
              {compat.map((c) => (
                <span key={c.key} className="ks-chip" title={`Works with ${c.label.toLowerCase()}`}>
                  {c.icon}{c.label}
                </span>
              ))}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: "auto", paddingTop: 4 }}>
            <span className="ks-mono" style={{ fontSize: 17, fontWeight: 500, color: K.ink }}>{price(item.rate ?? 0)}</span>
            {discount && (
              <span className="ks-mono" style={{ fontSize: 11, color: K.faint, textDecoration: "line-through" }}>
                {price(item.compareAtPrice ?? 0)}
              </span>
            )}
            {hasRating && (
              <span className="ks-mono" style={{ marginLeft: "auto", fontSize: 10.5, color: K.muted, display: "flex", alignItems: "center", gap: 3 }}>
                <Star size={10} fill={COPPER} color={COPPER} />{summary.avgRating.toFixed(1)}
              </span>
            )}
          </div>

          {q > 0 && !configurable ? (
            <div className="ks-step" onClick={(e) => e.stopPropagation()} style={{ borderColor: accent }}>
              <button aria-label="Remove one" style={{ background: accent }}
                onClick={(e) => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                <Minus size={13} color="#fff" />
              </button>
              <span className="ks-mono" style={{ fontSize: 13, color: accent }}>{q}</span>
              <button aria-label="Add one" style={{ background: accent }} onClick={(e) => handleAddToCart(item, e)}>
                <Plus size={13} color="#fff" />
              </button>
            </div>
          ) : (
            <button className="ks-add" onClick={(e) => handleAddToCart(item, e)}
              style={{ borderColor: accent, color: accent }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = accent; (e.currentTarget as HTMLElement).style.color = "#fff"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; (e.currentTarget as HTMLElement).style.color = accent; }}>
              {configurable ? <Sliders size={13} /> : <Plus size={14} />}
              {configurable ? "Choose options" : "Add to cart"}
            </button>
          )}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     RESULTS
  ═══════════════════════════════════ */
  const emptyState = (title: string, body: string) => (
    <div style={{ textAlign: "center", padding: "64px 20px" }}>
      <div style={{ width: 54, height: 54, borderRadius: "50%", background: ENAMEL, border: `1px solid ${K.grout}`, boxShadow: `inset 0 0 0 4px #fff, inset 0 0 0 5px ${K.groutSoft}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <ChefHat size={21} color={K.muted} />
      </div>
      <h3 className="ks-disp" style={{ fontSize: 24, color: K.ink, marginBottom: 8 }}>{title}</h3>
      <p style={{ color: K.muted, fontSize: 13, marginBottom: 22 }}>{body}</p>
      <button onClick={resetAllFilters} className="ks-hbtn" style={{ margin: "0 auto", padding: "10px 20px" }}>
        Clear filters
      </button>
    </div>
  );

  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <div className="ks-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: K.faint, textTransform: "uppercase" }}>
          On the shelf
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={activeSort} onChange={(e) => setActiveSort(e.target.value as SortKey)} aria-label="Sort"
            className="ks-mono"
            style={{ background: K.sheet, border: `1px solid ${K.grout}`, color: K.inkSoft, borderRadius: 8, padding: "6px 10px", fontSize: 11, cursor: "pointer", outline: "none" }}>
            <option value="default">Featured</option>
            <option value="newest">Newest</option>
            <option value="price_asc">Price ↑</option>
            <option value="price_desc">Price ↓</option>
            <option value="name_asc">Name A–Z</option>
          </select>
          {locationActive && (
            <button onClick={handleToggleGlobal} className="ks-hbtn">
              {showGlobal ? <MapPin size={12} /> : <Globe size={12} />}
              {showGlobal ? "Local" : "Global"}
            </button>
          )}
        </div>
      </div>

      {productsByBrand.length === 0 ? (
        filteredProducts.length === 0
          ? emptyState(
              compatFilter.size
                ? "Nothing here fits that hob"
                : activeFilterName ? `No ${activeFilterName} in stock` : `Nothing in ${cat?.name ?? "this drawer"} yet`,
              compatFilter.size
                ? "Drop one of the Works with filters, or try another category."
                : "Pick another tag on the rail above, or clear the filters."
            )
          : locationDenied
            ? emptyState("Location is off", "Turn it on to see makers who can ship to you.")
            : emptyState(
                showGlobal ? "No global makers here" : "No local makers here",
                `Switch to ${showGlobal ? "local" : "global"} above, or clear the filters.`
              )
      ) : (
        productsByBrand.map((group) => (
          <div key={group.brandId}>
            {makerRow(group.brandId)}
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => plateCard(item))}
            </div>
          </div>
        ))
      )}
    </div>
  );

  /* ═══════════════════════════════════
     DETAIL — the full spec card
  ═══════════════════════════════════ */
  const detailModal = () => {
    if (!detailItem) return null;
    const item = detailItem;
    const partner = brandMap.get(item.brandId);
    const accent = brandAccentColor(partner);
    const brandName = partner?.name;
    const img = item.photos?.[0];
    const stock = item.stock ?? 0;
    const soldOut = isOutOfStock(item);
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;

    const variantDefs = variantAttrsForItem(item);
    const facts = factsOf(item);
    const compat = compatOf(item);
    const sizeLine = sizeLineOf(item);
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;

    const allChosen = variantDefs.every((a: any) => selectedVariants[String(a.id)] != null);
    const canContinue = !soldOut && qty > 0 && qty <= stock && allChosen;

    const content = (
      <div style={{ background: K.tile, border: `1px solid ${K.grout}`, borderRadius: isMobile ? "14px 14px 0 0" : 14, overflow: "hidden", width: "100%", maxWidth: isMobile ? "100%" : 480, maxHeight: isMobile ? "88vh" : "86vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 3, background: accent, flexShrink: 0 }} />
        {isMobile && <div style={{ width: 34, height: 3, borderRadius: 2, background: K.copperEdge, margin: "10px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "16px 18px 0" }}>
          <div className="ks-plate" style={{ borderRadius: 12, border: `1px solid ${K.grout}`, marginBottom: 14 }}>
            {img
              ? <img src={img} alt={item.name} />
              : <Utensils size={36} color={K.faint} style={{ position: "relative", zIndex: 2 }} />}
            <button onClick={closeDetail} aria-label="Close"
              style={{ position: "absolute", zIndex: 3, top: 10, right: 10, width: 28, height: 28, borderRadius: 7, background: "rgba(255,255,255,.92)", border: `1px solid ${K.grout}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <X size={14} color={K.inkSoft} />
            </button>
            {discount && (
              <span className="ks-flag ks-mono" style={{ left: 10, background: FLAME, color: "#fff" }}>−{discount}%</span>
            )}
          </div>

          {brandName && (
            <p className="ks-mono" style={{ fontSize: 10, letterSpacing: ".16em", textTransform: "uppercase", color: accent, marginBottom: 4 }}>{brandName}</p>
          )}
          <div className="ks-disp" style={{ fontSize: 24, color: K.ink, marginBottom: 4, lineHeight: 1.1 }}>{item.name}</div>
          {item.categoryId && (
            <p className="ks-mono" style={{ fontSize: 10.5, color: K.faint, marginBottom: 12 }}>{catPathLabel(item.categoryId)}</p>
          )}

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <span className="ks-mono" style={{ fontSize: 22, fontWeight: 500, color: K.ink }}>{price(item.rate ?? 0)}</span>
            {discount && <span className="ks-mono" style={{ fontSize: 12, color: K.faint, textDecoration: "line-through" }}>{price(item.compareAtPrice ?? 0)}</span>}
            {sizeLine && <span className="ks-mono" style={{ fontSize: 11.5, color: K.muted }}>{sizeLine}</span>}
            {hasRating && (
              <span className="ks-mono" style={{ fontSize: 11, color: K.muted, display: "flex", alignItems: "center", gap: 4 }}>
                <Star size={11} fill={COPPER} color={COPPER} />
                {summary.avgRating.toFixed(1)} ({summary.totalReviews.toLocaleString()})
              </span>
            )}
          </div>

          {/* ── works with ── */}
          {compat.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div className="ks-mono" style={{ fontSize: 10, letterSpacing: ".18em", color: K.muted, textTransform: "uppercase", marginBottom: 8 }}>Works with</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {compat.map((c) => (
                  <span key={c.key} className="ks-chip mat" style={{ fontSize: 11, padding: "6px 11px" }}>
                    {c.icon}{c.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── options the maker offers ── */}
          {variantDefs.map((attr: any) => {
            const key = String(attr.id);
            const chosen = selectedVariants[key];
            const isColour = attr.inputType === "color";
            return (
              <div key={key} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 9 }}>
                  <span className="ks-mono" style={{ fontSize: 10, letterSpacing: ".18em", color: K.muted, textTransform: "uppercase" }}>{attr.name}</span>
                  {chosen == null && <span style={{ fontSize: 10, fontWeight: 500, color: K.flame }}>pick one</span>}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {attr.options.map((opt: any) => {
                    const on = String(chosen) === String(opt.id);
                    return (
                      <button key={opt.id} onClick={() => setSelectedVariants((p) => ({ ...p, [key]: opt.id }))}
                        style={{
                          display: "flex", alignItems: "center", gap: 7, padding: isColour ? "6px 12px 6px 7px" : "8px 13px",
                          borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 500,
                          border: `1px solid ${on ? COPPER : K.grout}`,
                          background: on ? K.copperSoft : K.sheet, color: on ? K.ink : K.inkSoft,
                        }}>
                        {isColour && (
                          <span style={{ width: 16, height: 16, borderRadius: "50%", background: opt.hex || K.copperSoft, border: "1px solid rgba(38,38,43,.15)" }} />
                        )}
                        {opt.name || opt.standard || String(opt.id)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* ── the spec card ── */}
          {facts.length > 0 && (
            <div style={{ border: `1px solid ${K.grout}`, borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
              <div className="ks-mono" style={{ fontSize: 10, letterSpacing: ".2em", color: K.muted, textTransform: "uppercase", padding: "10px 13px", borderBottom: `1px solid ${K.grout}`, background: K.copperSoft }}>
                Specifications
              </div>
              {facts.map((f, i) => (
                <div key={f.name} style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "9px 13px", background: i % 2 ? K.sheet : K.sheet2, fontSize: 12 }}>
                  <span style={{ color: K.muted }}>{f.name}</span>
                  <span className="ks-mono" style={{ color: K.ink, textAlign: "right" }}>{f.value}</span>
                </div>
              ))}
            </div>
          )}

          {item.vendor && (
            <p style={{ fontSize: 11.5, color: K.muted, marginBottom: 10 }}>
              <span style={{ color: K.inkSoft, fontWeight: 700 }}>Vendor:</span> {item.vendor}
            </p>
          )}
          {item.tags?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {item.tags.map((t: string) => (
                <span key={t} className="ks-mono" style={{ fontSize: 10, color: K.muted, background: K.sheet, border: `1px solid ${K.grout}`, borderRadius: 6, padding: "3px 8px" }}>#{t}</span>
              ))}
            </div>
          )}

          {item.description && (
            <div style={{ fontSize: 13, color: K.inkSoft, lineHeight: 1.7, marginBottom: 16 }}
              dangerouslySetInnerHTML={{ __html: item.description }} />
          )}

          {soldOut ? (
            <p className="ks-mono" style={{ fontSize: 11, color: K.faint, marginBottom: 12 }}>OUT OF STOCK</p>
          ) : stock <= 5 ? (
            <p className="ks-mono" style={{ fontSize: 11, color: K.flame, marginBottom: 12 }}>ONLY {stock} LEFT</p>
          ) : null}

          {!soldOut && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <span className="ks-mono" style={{ fontSize: 10, letterSpacing: ".18em", color: K.muted }}>QTY</span>
              <div style={{ display: "flex", border: `1px solid ${K.grout}`, borderRadius: 8, overflow: "hidden" }}>
                <button onClick={() => setQty((v) => Math.max(1, v - 1))} aria-label="Decrease"
                  style={{ width: 38, height: 38, border: "none", background: K.sheet, cursor: "pointer", color: K.inkSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Minus size={13} />
                </button>
                <div className="ks-mono" style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: K.ink, background: K.sheet2 }}>{qty}</div>
                <button onClick={() => setQty((v) => Math.min(stock || 1, v + 1))} disabled={qty >= stock} aria-label="Increase"
                  style={{ width: 38, height: 38, border: "none", background: K.sheet, cursor: qty >= stock ? "not-allowed" : "pointer", color: K.inkSoft, opacity: qty >= stock ? .4 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={13} />
                </button>
              </div>
              <span className="ks-mono" style={{ fontSize: 12, color: K.muted, marginLeft: "auto" }}>
                {price((item.rate ?? 0) * qty)}
              </span>
            </div>
          )}
        </div>

        <div style={{ padding: "13px 18px 18px", display: "flex", gap: 9, flexShrink: 0, borderTop: `1px solid ${K.grout}` }}>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(false)}
            style={{ flex: 1, padding: "12px 0", borderRadius: 8, border: `1px solid ${canContinue ? accent : K.grout}`, background: "transparent", color: canContinue ? accent : K.faint, fontWeight: 700, fontSize: 12.5, cursor: canContinue ? "pointer" : "not-allowed" }}>
            Add to cart
          </button>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(true)}
            style={{ flex: 1.15, padding: "12px 0", borderRadius: 8, border: "none", background: canContinue ? accent : K.groutSoft, color: canContinue ? "#fff" : K.faint, fontWeight: 700, fontSize: 12.5, cursor: canContinue ? "pointer" : "not-allowed" }}>
            Buy now
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeDetail}
          style={{ position: "fixed", inset: 0, background: "rgba(38,38,43,.55)", backdropFilter: "blur(5px)", zIndex: 500 }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={(e) => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>
              {content}
            </motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={(e) => e.stopPropagation()}>
              <motion.div initial={{ scale: .96, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: .96, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }} style={{ width: 480, maxWidth: "100%" }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  const locationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(38,38,43,.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <motion.div initial={{ scale: .94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .94, y: 20 }} onClick={(e) => e.stopPropagation()}
            className="ks-panel" style={{ width: 320, padding: 22, textAlign: "center" }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: K.copperSoft, border: `1px solid ${K.copperEdge}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
              <MapPin size={19} color={COPPER} />
            </div>
            <h3 className="ks-disp" style={{ fontSize: 22, color: K.ink, marginBottom: 8 }}>Location is off</h3>
            <p style={{ color: K.muted, fontSize: 12.5, lineHeight: 1.6, marginBottom: 18 }}>
              Allow location in your browser settings to see makers who can ship to you.
            </p>
            <button onClick={() => setLocationModalOpen(false)} className="ks-hbtn" style={{ margin: "0 auto", padding: "10px 22px" }}>
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const bottomTabs = () => (isMobile && userId ? (
    <div style={S.tabs}>
      {[
        { icon: <Home size={20} />, label: "Home", on: false, go: () => navigate(`/StoreProductsHome/${userId}`) },
        { icon: <UtensilsCrossed size={20} />, label: "Kitchen", on: true, go: () => navigate("/KitchenStore") },
        {
          label: "Cart", on: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={20} />
              {cartTotal > 0 && (
                <span className="ks-mono" style={{ position: "absolute", top: -5, right: -7, background: FLAME, color: "#fff", fontSize: 8, minWidth: 15, height: 15, padding: "0 3px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal}</span>
              )}
            </div>
          ), go: goToCheckout,
        },
        { icon: <ShoppingBasket size={20} />, label: "Makers", on: false, go: () => navigate("/Stores") },
        { icon: <User size={20} />, label: "Profile", on: false, go: () => navigate(`/profile/${userId}`) },
      ].map((t) => (
        <button key={t.label} onClick={t.go} className={`ks-tab${t.on ? " on" : ""}`}>
          {React.cloneElement(t.icon as any, { color: t.on ? K.ink : K.faint })}
          <span>{t.label}</span>
        </button>
      ))}
    </div>
  ) : null);

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div className="ks">
      <style>{GLOBAL_CSS}</style>

      <header className="ks-rail">
        <div className="ks-edge" />
        {railRow()}
        <div style={{ padding: isMobile ? "0 14px" : "0 22px" }}>{!isMobile && hangingRail()}</div>
      </header>

      {mobileBrowseSheet()}

      <div style={{ maxWidth: 1320, margin: "0 auto", padding: isMobile ? "14px 14px 0" : "22px 22px 0" }}>
        {isMobile && <div style={{ marginBottom: 6 }}>{hangingRail()}</div>}
        {heading()}
        {compatBar()}

        <div style={{ display: "grid", gridTemplateColumns: !isMobile && desktopCatOpen ? "252px 1fr" : "1fr", gap: 20, alignItems: "start" }}>
          {!isMobile && desktopCatOpen && (
            <aside style={{ position: "sticky", top: 140, maxHeight: "calc(100vh - 162px)", overflowY: "auto" }}>
              {browsePanel()}
            </aside>
          )}
          <main style={{ minWidth: 0 }}>
            {drawerTiles()}
            {renderProducts(isMobile)}
          </main>
        </div>
      </div>

      {detailModal()}
      {locationModal()}
      
    </div>
  );
};

export default KitchenStore;
